// frontend/app/components/lessonPlans/blocks/LessonBlockShell.js
//
// Small shared primitives every lesson block builds on, so no individual block
// re-invents card chrome or reaches for a raw hex.

import React from "react";
import { StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

export const BlockCard = ({ colors, children, style, accent }) => (
  <View
    style={[
      styles.card,
      {
        backgroundColor: colors.panelBg,
        borderColor: colors.border,
      },
      accent ? { borderLeftWidth: 3, borderLeftColor: accent } : null,
      style,
    ]}
  >
    {children}
  </View>
);

export const BlockHeading = ({ colors, icon, title, accent, right }) => {
  if (!title && !icon) return null;
  return (
    <View style={styles.headingRow}>
      {icon ? (
        <MaterialCommunityIcons
          name={icon}
          size={18}
          color={accent || colors.textSecondary}
          style={styles.headingIcon}
        />
      ) : null}
      <Text style={[styles.headingText, { color: colors.textPrimary }]}>
        {title}
      </Text>
      {right ? <View style={styles.headingRight}>{right}</View> : null}
    </View>
  );
};

export const BlockPrompt = ({ colors, children }) => (
  <Text style={[styles.prompt, { color: colors.textPrimary }]}>{children}</Text>
);

export const BlockBody = ({ colors, children, style }) => (
  <Text style={[styles.body, { color: colors.textSecondary }, style]}>
    {children}
  </Text>
);

export const Pill = ({ colors, label, accent, background, icon }) => (
  <View
    style={[
      styles.pill,
      {
        backgroundColor: background || colors.subtleBg,
        borderColor: accent || colors.border,
      },
    ]}
  >
    {icon ? (
      <MaterialCommunityIcons
        name={icon}
        size={12}
        color={accent || colors.textSecondary}
        style={{ marginRight: 4 }}
      />
    ) : null}
    <Text style={[styles.pillText, { color: accent || colors.textSecondary }]}>
      {label}
    </Text>
  </View>
);

/**
 * Feedback shown after a graded check is answered. `isCorrect === null` means
 * the block is not auto-graded (a prediction or free-text answer), which is
 * acknowledged rather than marked right or wrong.
 */
export const AnswerFeedback = ({ colors, isCorrect, explanation, t }) => {
  const translate = t || ((value) => value);
  const ungraded = isCorrect === null || isCorrect === undefined;

  const accent = ungraded
    ? colors.info
    : isCorrect
      ? colors.success
      : colors.danger;
  const background = ungraded
    ? colors.subtleBg
    : isCorrect
      ? colors.correctBg
      : colors.incorrectBg;
  const border = ungraded
    ? colors.border
    : isCorrect
      ? colors.correctBorder
      : colors.incorrectBorder;
  const icon = ungraded
    ? "check-circle-outline"
    : isCorrect
      ? "check-circle"
      : "close-circle";
  const label = ungraded
    ? translate("Answer recorded")
    : isCorrect
      ? translate("Correct")
      : translate("Not quite");

  return (
    <View
      style={[
        styles.feedback,
        { backgroundColor: background, borderColor: border },
      ]}
    >
      <View style={styles.feedbackHeader}>
        <MaterialCommunityIcons name={icon} size={16} color={accent} />
        <Text style={[styles.feedbackLabel, { color: accent }]}>{label}</Text>
      </View>
      {explanation ? (
        <Text style={[styles.feedbackText, { color: colors.textSecondary }]}>
          {explanation}
        </Text>
      ) : null}
    </View>
  );
};

export const ActionButton = ({
  colors,
  label,
  icon,
  accent,
  onPress,
  disabled,
  variant = "solid",
  size = "regular",
}) => {
  const solid = variant === "solid";
  const large = size === "large";
  const activeAccent = accent || colors.accentPrimary;

  return (
    <TouchableOpacity
      onPress={onPress}
      disabled={disabled}
      accessibilityRole="button"
      accessibilityLabel={label}
      style={[
        styles.action,
        large ? styles.actionLarge : null,
        {
          backgroundColor: solid ? activeAccent : "transparent",
          borderColor: activeAccent,
          opacity: disabled ? 0.5 : 1,
        },
      ]}
    >
      {icon ? (
        <MaterialCommunityIcons
          name={icon}
          size={large ? 20 : 16}
          color={solid ? "#ffffff" : activeAccent}
          style={{ marginRight: 6 }}
        />
      ) : null}
      <Text
        style={[
          styles.actionText,
          large ? styles.actionTextLarge : null,
          { color: solid ? "#ffffff" : activeAccent },
        ]}
      >
        {label}
      </Text>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  card: {
    borderRadius: 14,
    borderWidth: 1,
    padding: 16,
    marginBottom: 12,
  },
  headingRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
  },
  headingIcon: { marginRight: 8 },
  headingText: { fontSize: 15, fontWeight: "700", flexShrink: 1 },
  headingRight: { marginLeft: "auto", paddingLeft: 8 },
  prompt: {
    fontSize: 15,
    fontWeight: "600",
    lineHeight: 22,
    marginBottom: 12,
  },
  body: { fontSize: 14, lineHeight: 22 },
  pill: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 999,
    borderWidth: 1,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  pillText: { fontSize: 11, fontWeight: "700", letterSpacing: 0.3 },
  feedback: {
    marginTop: 12,
    borderRadius: 10,
    borderWidth: 1,
    padding: 12,
  },
  feedbackHeader: { flexDirection: "row", alignItems: "center", gap: 6 },
  feedbackLabel: { fontSize: 13, fontWeight: "700", marginLeft: 6 },
  feedbackText: { fontSize: 13, lineHeight: 20, marginTop: 6 },
  action: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 10,
    borderWidth: 1,
    paddingHorizontal: 14,
    paddingVertical: 9,
  },
  actionText: { fontSize: 13, fontWeight: "700" },
  actionLarge: { paddingHorizontal: 22, paddingVertical: 13, borderRadius: 12 },
  actionTextLarge: { fontSize: 15 },
});
